import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Smartphone,
  Copy,
  CheckCircle2,
  Users,
  ShieldCheck,
  KeyRound,
  Apple,
  Play,
  Mail,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Aplicația Mobilă — admin control panel.  Mirrors the web-app deployment
 * card but for the React Native / Expo build:
 *   - Live Expo tunnel URL + scannable QR (refreshable).
 *   - Step-by-step install guide.
 *   - Per-tenant stats (members activated, biometric on, pending pwd).
 *   - Production store links (placeholders until the build is published).
 */
export default function MobileAppPage() {
  const { t, i18n } = useTranslation();
  const locale =t("mobileApp.en_us");
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [manualUrl, setManualUrl] = useState("");
  const [savingManual, setSavingManual] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/mobile-app/info");
      setInfo(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 15s so that whenever the Expo keep-alive script
  // pushes a new tunnel URL (it rotates every restart), this page picks
  // it up without any manual action from the admin.
  useEffect(() => {
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const restartTunnel = null; // dev tunnel controls removed per user request
  const [shareFor, setShareFor] = useState(null); // "ios" | "android" | null

  const copyUrl = async () => {
    if (!info?.tunnel?.expo_url) return;
    try {
      await navigator.clipboard.writeText(info.tunnel.expo_url);
      setCopied(true);
      toast.success(t("mobileAdmin.copied", "URL copiat — lipește-l în Expo Go."));
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error(t("mobileAdmin.clipboardError"));
    }
  };

  const saveManualUrl = async () => {
    const url = manualUrl.trim();
    if (!url) return;
    setSavingManual(true);
    try {
      await api.post("/admin/mobile-app/tunnel-url", { expo_url: url });
      toast.success(t("mobileAdmin.manualSaved"));
      setManualUrl("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingManual(false);
    }
  };

  const clearManualUrl = async () => {
    if (!window.confirm(t("mobileAdmin.manualClearConfirm"))) return;
    setSavingManual(true);
    try {
      await api.delete("/admin/mobile-app/tunnel-url");
      toast.success(t("mobileAdmin.manualCleared"));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingManual(false);
    }
  };

  const tunnelOnline = !!info?.tunnel?.online;
  const tunnelManual = info?.tunnel?.source === "manual";

  return (
    <div className="page-wrap" data-testid="mobile-app-page">
      <header className="page-head">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Smartphone size={20} /> {t("mobileAdmin.title", "Aplicația Mobilă")}
          </h1>
        </div>
      </header>

      <div className="grid gap-5 mt-4" style={{ gridTemplateColumns: "minmax(0, 1.1fr) minmax(0, 1fr)" }}>
        {/* Security notice — clarifies that mobile creds CANNOT log into the web admin */}
        <div
          className="alert"
          style={{
            gridColumn: "1 / -1",
            background: "#eef6ff",
            border: "1px solid #2960a8",
            color: "#1d3a6e",
            display: "flex",
            gap: 10,
            alignItems: "flex-start",
          }}
        >
          <ShieldCheck size={16} style={{ marginTop: 2, flexShrink: 0 }} />
          <div>
            <p style={{ fontWeight: 700, fontSize: "0.85rem" }}>
              {t("mobileAdmin.securityTitle", "Separare strictă: conturi mobil ≠ conturi admin web")}
            </p>
            <p className="text-xs" style={{ marginTop: 4 }}>
              {t(
                "mobileAdmin.securityBody",
                "Credentialele generate aici sunt valide DOAR în aplicația mobilă. Membrii nu se pot loga în panoul web cu emailul și parola lor. Pentru acces admin se folosesc conturi separate (Utilizatori → Adaugă admin)."
              )}
            </p>
          </div>
        </div>

        {/* INSTALL — public store buttons + QR code for App Store
            (iOS) / Google Play (Android). The dev-mode Expo Go QR has
            been removed — brothers should download the official
            published apps, not the dev tunnel. */}
        <section className="card-gold" style={{ padding: "1.25rem" }}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="section-title" style={{ marginBottom: 0 }}>
              {t("mobileAdmin.installCard", "INSTALARE — DESCARCĂ APLICAȚIA")}
            </h2>
          </div>

          <div style={{ flex: 1, minWidth: 240 }}>
            <p className="kv-label" style={{ marginBottom: 10 }}>
              {t("mobileApp.download_square_compass_on_your")}
            </p>

            {/* Official store links — no QR codes, no direct .APK: brothers
                install only the published store builds. */}
            <div className="flex flex-wrap gap-3" data-testid="store-buttons">
              <StoreBadge
                icon={<Apple size={18} />}
                small="App Store"
                label="iOS"
                url={info?.production?.ios_app_store_url}
                testId="store-link-ios"
              />
              <StoreBadge
                icon={<Play size={18} />}
                small="Google Play"
                label="Android"
                url={info?.production?.android_play_store_url}
                testId="store-link-android"
              />
            </div>

            <div className="flex flex-wrap items-center gap-3" style={{ marginTop: "0.9rem" }}>
              <button
                className="btn-outline-gold flex items-center gap-1"
                onClick={() => setShareFor("both")}
                data-testid="share-email-both"
                style={{ fontSize: "0.72rem" }}
              >
                <Mail size={11} /> {t("mobileAdmin.shareEmail", "TRIMITE PE EMAIL")}
              </button>
              <span className="kv-label" style={{ fontSize: "0.68rem" }}>
                {t("mobileAdmin.shareBothHint", "Membrul primește ambele linkuri (App Store + Google Play) și credențialele.")}
              </span>
            </div>

            <ol className="mobile-steps" style={{ marginTop: "1rem" }}>
              <li>
                {t("mobileApp.open_the_app_store_google")}
              </li>
              <li>
                {t("mobileApp.install_square_compass")}
              </li>
              <li>
                {t("mobileApp.sign_in_with_the_brother")}
              </li>
            </ol>
          </div>
        </section>

        {/* Stats */}
        <section className="card-gold" style={{ padding: "1.25rem" }}>
          <h2 className="section-title">
            {t("mobileAdmin.statsCard", "ADOPȚIE")}
            {info?.scope === "lodge" && (
              <span className="kv-label" style={{ marginLeft: 8, fontSize: "0.7rem" }}>
                {t("mobileAdmin.scopeLodge", "(loja ta)")}
              </span>
            )}
          </h2>

          <div className="grid grid-cols-2 gap-3" data-testid="mobile-stats">
            <StatCard
              icon={<Users size={16} />}
              label={t("mobileAdmin.statTotal", "Frați total")}
              value={info?.stats?.members_total ?? "—"}
            />
            <StatCard
              icon={<CheckCircle2 size={16} />}
              label={t("mobileAdmin.statActivated", "Aplicație activată")}
              value={info?.stats?.app_activated ?? "—"}
              accent
            />
            <StatCard
              icon={<ShieldCheck size={16} />}
              label={t("mobileAdmin.statBiometric", "Face ID / amprentă")}
              value={info?.stats?.biometric_enabled ?? "—"}
            />
            <StatCard
              icon={<KeyRound size={16} />}
              label={t("mobileAdmin.statPendingPwd", "Parolă temporară")}
              value={info?.stats?.pending_password_change ?? "—"}
              warn
            />
          </div>
        </section>
      </div>

      {info?.checked_at && (
        <p className="kv-label" style={{ marginTop: 16, textAlign: "right", fontSize: "0.7rem" }}>
          {t("mobileAdmin.checkedAt")}: {new Date(info.checked_at).toLocaleString(locale)}
        </p>
      )}

      <MembersTable />

      {shareFor && <ShareEmailModal platform={shareFor} onClose={() => setShareFor(null)} />}

      <style>{`
        .status-pill {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 4px 10px; border-radius: 999px; font-size: 0.7rem;
          letter-spacing: 1px; font-weight: 600; text-transform: uppercase;
          border: 1px solid;
        }
        .status-pill--ok { color: #1f7a3a; border-color: #1f7a3a; background: #e7f6ec; }
        .status-pill--off { color: #8a2a2a; border-color: #8a2a2a; background: #fbe9e9; }
        .status-dot { width: 7px; height: 7px; border-radius: 999px; display: inline-block; }
        .status-dot--ok { background: #1f7a3a; box-shadow: 0 0 0 3px rgba(31,122,58,0.18); }
        .status-dot--off { background: #8a2a2a; box-shadow: 0 0 0 3px rgba(138,42,42,0.18); }
        .mobile-steps { padding-left: 1.1rem; color: #34322d; font-size: 0.83rem; line-height: 1.55; }
        .mobile-steps li { margin-bottom: 4px; }
        .link-gold { color: #8a6f1c; text-decoration: underline; font-weight: 600; }
        .alert { padding: 0.85rem 1rem; border-radius: 4px; }
        .alert-warn { background: #fdf4e0; border: 1px solid #c9a84c; color: #34322d; }
        .stat-card {
          padding: 0.85rem; border: 1px solid #ddc77a55; border-radius: 4px;
          background: #fdf9ec;
        }
        .stat-card--accent { border-color: #c9a84c; background: #fdf4e0; }
        .stat-card--warn { border-color: #c9852f55; background: #fcefe0; }
        .stat-card .stat-icon {
          display: inline-flex; align-items: center; justify-content: center;
          color: #8a6f1c; margin-bottom: 4px;
        }
        .stat-card .stat-value {
          font-family: Cinzel, serif; font-size: 1.6rem; font-weight: 700; color: #0d1b2a;
          line-height: 1; margin-top: 2px;
        }
        .stat-card .stat-label {
          color: #34322d; font-size: 0.72rem; letter-spacing: 1px;
          text-transform: uppercase; margin-top: 6px;
        }
        .store-row {
          display: flex; align-items: center; justify-content: space-between;
          padding: 8px 12px; border: 1px solid #ddc77a; border-radius: 4px;
          background: #fdf9ec;
        }
        .store-row .store-link {
          color: #8a6f1c; font-weight: 600; font-size: 0.78rem;
          display: inline-flex; align-items: center; gap: 4px;
        }
        .store-row .store-placeholder {
          color: #8a6f1c88; font-size: 0.75rem; font-style: italic;
        }
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        .grade-pill {
          margin-left: 8px; padding: 2px 8px; border-radius: 999px;
          font-size: 0.65rem; letter-spacing: 1px; text-transform: uppercase;
          color: #8a6f1c; background: #fdf4e0; border: 1px solid #c9a84c;
        }
        .mobile-members-table-wrap { overflow-x: auto; border: 1px solid #ddc77a55; border-radius: 4px; }
        .mobile-members-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
        .mobile-members-table th {
          text-align: left; padding: 0.7rem 0.85rem; background: #fdf9ec;
          color: #8a6f1c; font-family: Cinzel, serif; font-size: 0.7rem; letter-spacing: 1.5px;
          font-weight: 700; border-bottom: 1px solid #ddc77a;
        }
        .mobile-members-table td {
          padding: 0.65rem 0.85rem; border-bottom: 1px solid #eee2c0;
          color: #34322d; vertical-align: middle;
        }
        .mobile-members-table tr:last-child td { border-bottom: none; }
        .mobile-members-table tr:hover td { background: #fdf9ec; }
        .pill {
          display: inline-flex; align-items: center; gap: 4px;
          padding: 3px 9px; border-radius: 999px; font-size: 0.7rem;
          letter-spacing: 0.5px; font-weight: 600; border: 1px solid;
        }
        .pill--ok { color: #1f7a3a; border-color: #1f7a3a; background: #e7f6ec; }
        .pill--warn { color: #8a6f1c; border-color: #c9a84c; background: #fdf4e0; }
        .pill--off { color: #6b6b6b; border-color: #cfcfcf; background: #f3f3f3; }
        .cred-row { display: flex; gap: 12px; padding: 4px 0; align-items: baseline; }
        .cred-label { font-family: Cinzel, serif; font-size: 0.7rem; letter-spacing: 1.5px; color: #8a6f1c; min-width: 64px; }
        .cred-value { font-family: monospace; font-size: 0.85rem; color: #0d1b2a; word-break: break-all; }
      `}</style>
    </div>
  );
}

function StatCard({ icon, label, value, accent, warn }) {
  const cls = `stat-card ${accent ? "stat-card--accent" : ""} ${warn ? "stat-card--warn" : ""}`.trim();
  return (
    <div className={cls}>
      <div className="stat-icon">{icon}</div>
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

function StoreBadge({ icon, small, label, url, testId }) {
  const { t } = useTranslation();
  if (!url) {
    return (
      <span className="store-placeholder" data-testid={`${testId}-pending`}>
        {label} — {t("mobileAdmin.notPublished", "În curând")}
      </span>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      data-testid={testId}
      style={{
        display: "inline-flex", alignItems: "center", gap: 10, minWidth: 190,
        padding: "8px 16px", borderRadius: 8, background: "#0d1b2a", color: "#fff",
        textDecoration: "none", border: "1px solid #c9a84c",
      }}
    >
      {icon}
      <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
        <span style={{ fontSize: "0.62rem", letterSpacing: "0.08em", opacity: 0.8 }}>{t("mobileAdmin.availableOn", "Disponibil pe")}</span>
        <span style={{ fontSize: "0.95rem", fontWeight: 700 }}>{small}</span>
      </span>
    </a>
  );
}

function ShareEmailModal({ platform, onClose }) {
  const { t } = useTranslation();
  const [members, setMembers] = useState([]);
  const [memberId, setMemberId] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/admin/mobile-app/members")
      .then(({ data }) => setMembers((data || []).filter((m) => m.email)))
      .catch((err) => toast.error(formatApiError(err)));
  }, []);

  const pick = (id) => {
    setMemberId(id);
    const m = members.find((x) => x.member_id === id);
    setEmail(m?.email || "");
  };

  const send = async () => {
    if (!memberId) { toast.error(t("mobileAdmin.shareNoMember", "Alege membrul.")); return; }
    setSending(true);
    try {
      await api.post("/admin/mobile-app/share-email", { member_id: memberId, email: email.trim(), platform });
      toast.success(t("mobileAdmin.shareSent", "Email trimis — membrul primește linkul de download și credentialele."));
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="dialog-overlay" onClick={onClose} data-testid="share-email-modal">
      <div className="dialog-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <h3 className="section-title flex items-center gap-2">
          <Mail size={15} /> {t("mobileAdmin.shareTitle", "Trimite aplicația pe email")} — App Store + Google Play
        </h3>
        <div className="mt-3">
          <label className="label-lodge">{t("mobileAdmin.shareMember", "Membrul (primește credentialele)")} *</label>
          <select
            className="input-lodge"
            value={memberId}
            onChange={(e) => pick(e.target.value)}
            data-testid="share-email-member-select"
          >
            <option value="">—</option>
            {members.map((m) => (
              <option key={m.member_id} value={m.member_id}>
                {m.first_name} {m.last_name} ({m.email})
              </option>
            ))}
          </select>
        </div>
        <div className="mt-3">
          <label className="label-lodge">{t("mobileAdmin.shareEmailAddr", "Trimite către adresa de email")} *</label>
          <input
            className="input-lodge"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@exemplu.ro"
            data-testid="share-email-address-input"
          />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button className="btn-outline-gold" onClick={onClose}>{t("common.cancel", "Anulează")}</button>
          <button
            className="btn-primary-gold flex items-center gap-1"
            onClick={send}
            disabled={sending || !memberId || !email.trim()}
            data-testid="share-email-send-btn"
          >
            <Mail size={12} /> {sending ? "..." : t("mobileAdmin.shareSend", "TRIMITE")}
          </button>
        </div>
      </div>
    </div>
  );
}

function MembersTable() {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [issuing, setIssuing] = useState(null);
  const [creds, setCreds] = useState(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/mobile-app/members");
      setRows(data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const issue = async (memberId, label) => {
    setIssuing(memberId);
    try {
      const { data } = await api.post(`/admin/members/${memberId}/issue-activation`);
      setCreds({ ...data, member_label: label });
      toast.success(t("mobileAdmin.issued", "Credențiale generate. Le vezi o singură dată!"));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setIssuing(null);
    }
  };

  const copyAll = async () => {
    if (!creds) return;
    try {
      await navigator.clipboard.writeText(`${t("mobileAdmin.credEmail")} ${creds.username}\n${t("mobileAdmin.credPassword")} ${creds.temp_password}`);
      toast.success(t("mobileAdmin.copiedAll"));
    } catch {}
  };

  const filtered = (rows || []).filter((r) =>
    !filter.trim() ||
    `${r.first_name} ${r.last_name} ${r.email || ""}`.toLowerCase().includes(filter.trim().toLowerCase())
  );

  return (
    <section className="card-gold" style={{ padding: "1.25rem", marginTop: 20 }}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="section-title" style={{ marginBottom: 0 }}>
          {t("mobileAdmin.membersCard", "MEMBRI ȘI STAREA CONTULUI MOBIL")}
        </h2>
        <input
          type="search"
          placeholder={t("mobileAdmin.searchMember", "Caută frate / email...")}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="input-gold"
          style={{ minWidth: 260 }}
          data-testid="mobile-members-search"
        />
      </div>

      {busy && !rows ? (
        <p className="kv-label" style={{ padding: "1rem" }}>{t("mobileAdmin.loading")}</p>
      ) : !filtered.length ? (
        <p className="kv-label" style={{ padding: "1rem" }}>
          {filter.trim() ? t("mobileAdmin.noMatch", "Niciun rezultat.") : t("mobileAdmin.noMembers", "Nu există frați în baza de date.")}
        </p>
      ) : (
        <div className="mobile-members-table-wrap">
          <table className="mobile-members-table" data-testid="mobile-members-table">
            <thead>
              <tr>
                <th>{t("mobileAdmin.colName", "Frate")}</th>
                <th>{t("mobileAdmin.colEmail", "Email")}</th>
                <th>{t("mobileAdmin.colStatus", "Status mobil")}</th>
                <th style={{ textAlign: "right" }}>{t("mobileAdmin.colAction", "Acțiune")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const noEmail = !r.email;
                return (
                  <tr key={r.member_id} data-testid={`member-row-${r.member_id}`}>
                    <td>
                      <span style={{ fontWeight: 600, color: "#0d1b2a" }}>
                        {t("mobileAdmin.brotherShort")} {r.first_name} {r.last_name}
                      </span>
                      {r.grade && <span className="grade-pill">{r.grade}</span>}
                    </td>
                    <td>
                      {noEmail ? (
                        <span style={{ color: "#8a2a2a", fontStyle: "italic" }}>
                          {t("mobileAdmin.noEmail", "fără email — adaugă unul în profil")}
                        </span>
                      ) : (
                        <span style={{ fontFamily: "monospace", fontSize: "0.8rem", color: "#34322d" }}>{r.email}</span>
                      )}
                    </td>
                    <td>
                      <StatusPill status={r.status} biometric={r.biometric_enabled} />
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <button
                        className="btn-outline-gold flex items-center gap-1"
                        style={{ marginLeft: "auto" }}
                        onClick={() => issue(r.member_id, `${r.first_name} ${r.last_name}`)}
                        disabled={noEmail || issuing === r.member_id}
                        data-testid={`issue-${r.member_id}`}
                      >
                        <KeyRound size={11} />
                        {issuing === r.member_id
                          ? "..."
                          : r.status === "none"
                            ? t("mobileAdmin.generate", "GENEREAZĂ")
                            : t("mobileAdmin.regenerate", "REGENEREAZĂ")}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {creds && (
        <div
          style={{
            marginTop: 16,
            background: "#fdf4e0",
            border: "1px solid #c9a84c",
            padding: "0.85rem 1rem",
          }}
          data-testid="issued-creds"
        >
          <p style={{ fontFamily: "Cinzel, serif", fontSize: "0.85rem", color: "#0d1b2a", letterSpacing: "0.1em", marginBottom: 8 }}>
            {t("mobileAdmin.credsTitle", { name: creds.member_label?.toUpperCase() })}
          </p>
          <div className="cred-row">
            <span className="cred-label">{t("mobileAdmin.credEmail")}</span>
            <span className="cred-value">{creds.username}</span>
          </div>
          <div className="cred-row">
            <span className="cred-label">{t("mobileAdmin.credPassword")}</span>
            <span className="cred-value">{creds.temp_password}</span>
          </div>
          <div className="flex gap-2 mt-2">
            <button className="btn-primary-gold flex items-center gap-1" onClick={copyAll}>
              <Copy size={11} /> {t("mobileAdmin.copyAll")}
            </button>
            <button className="btn-outline-gold" onClick={() => setCreds(null)}>{t("mobileAdmin.credClose")}</button>
          </div>
          <p className="kv-label" style={{ fontSize: "0.7rem", marginTop: 8, color: "#8a2a2a", fontStyle: "italic" }}>
            {t("mobileAdmin.credsWarning")}
          </p>
        </div>
      )}
    </section>
  );
}

function StatusPill({ status, biometric }) {
  const { t } = useTranslation();
  const map = {
    active: { label: t("mobileAdmin.statusActive"), cls: "ok" },
    temp_pwd: { label: t("mobileAdmin.statusTemp"), cls: "warn" },
    none: { label: t("mobileAdmin.statusNone"), cls: "off" },
  };
  const cfg = map[status] || map.none;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      <span className={`pill pill--${cfg.cls}`}>
        <span className={`status-dot status-dot--${cfg.cls === "ok" ? "ok" : "off"}`} />
        {cfg.label}
      </span>
      {biometric && (
        <span className="pill pill--ok" title={t("mobileAdmin.statBiometric")}>
          <ShieldCheck size={10} /> {t("mobileAdmin.biometric")}
        </span>
      )}
    </span>
  );
}

